/**
 * 2D affine maps as OpenLayers and SVG write them: [a, b, c, d, e, f] with
 *
 *   x' = a·x + c·y + e,   y' = b·x + d·y + f
 *
 * — the six numbers of an SVG `matrix(a b c d e f)`. Used to move a whole
 * overlay layer from the view its shapes were rendered for to the view the
 * map just painted, without touching a single shape.
 */
export type Affine = readonly [number, number, number, number, number, number];

/** The affine map behind a point function, from three points: where the
 *  origin and the two unit steps land fix all six numbers. Null when the
 *  function cannot answer (no frame painted yet). */
export function probeAffine(
  map: (x: number, y: number) => { x: number; y: number } | null,
): Affine | null {
  const o = map(0, 0);
  const i = map(1, 0);
  const j = map(0, 1);
  if (!o || !i || !j) return null;
  return [i.x - o.x, i.y - o.y, j.x - o.x, j.y - o.y, o.x, o.y];
}

/** Exactly equal — the same view painted twice yields the same six numbers. */
export function sameAffine(a: Affine, b: Affine): boolean {
  for (let k = 0; k < 6; k++) if (a[k] !== b[k]) return false;
  return true;
}

/** outer ∘ inner: apply `inner` first, then `outer`. */
export function multiplyAffine(outer: Affine, inner: Affine): Affine {
  const [a, b, c, d, e, f] = outer;
  const [g, h, i, j, k, l] = inner;
  return [a * g + c * h, b * g + d * h, a * i + c * j, b * i + d * j, a * k + c * l + e, b * k + d * l + f];
}

/** Null for a degenerate map (zero area), which has no inverse. */
export function invertAffine(t: Affine): Affine | null {
  const [a, b, c, d, e, f] = t;
  const det = a * d - b * c;
  if (!det || !Number.isFinite(det)) return null;
  return [d / det, -b / det, -c / det, a / det, (c * f - d * e) / det, (b * e - a * f) / det];
}

export function applyAffine(t: Affine, x: number, y: number): { x: number; y: number } {
  return { x: t[0] * x + t[2] * y + t[4], y: t[1] * x + t[3] * y + t[5] };
}

/** The SVG `transform` attribute for the map. */
export function svgMatrix(t: Affine): string {
  return `matrix(${t.join(' ')})`;
}
